import {
  useEffect,
  useRef,
  useState
} from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Image,
  Skeleton,
  Stack,
  Text
} from '@chakra-ui/react'
import {
  getEventData,
  getSavedEvents,
  handleOpenModal,
  saveEvent,
  setEventComments,
  setEventCommentTree,
  setHideChat,
  setGetSavedEventsStatus,
  setHideNotification,
  setOpenPopupBlogs,
  setOpenPopupEvents
} from 'store'
import { COLORS } from 'utils/styles'
import { ImagesPng } from 'assets/images'
import { CloseIcon } from 'assets/icons'
import { RotatingAffinityText } from 'common/Buttons'
import { LoadingSpinier } from 'common/Loading'
import {
  convertToOwnTimeZone,
  decimalToDMS,
  isNotMobile
} from 'utils/helpers'
import {
  MODAL_SECTION,
  PENDING,
  SUCCEEDED
} from 'utils/constants'

export const SavedEvents = ({
  getSavedEventsStatus,
  openPopupBlogs,
  savedEvents
}) => {
  const dispatch = useDispatch()
  const observer = useRef()
  const secondToLastItem = useRef(null)

  const [loadedBG, setLoadedBG] = useState({})
  const [loadingMore, setLoadingMore] = useState(false)
  const [oldItemsCount, setOldItemsCount] = useState(0)
  const itemCountPerBatch = Math.ceil(window.innerHeight / 90)

  const handleBG = (id) => {
    setLoadedBG((prev) => ({
      ...prev,
      [id]: true
    }))
  }

  useEffect(() => {
    setTimeout(() => {
      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting && savedEvents.length - oldItemsCount >= itemCountPerBatch
        ) {
          observer.current.unobserve(secondToLastItem.current)
          setLoadingMore(true)
          setOldItemsCount(savedEvents.length)
          dispatch(getSavedEvents({
            lastId: savedEvents.at(-1).id,
            limit: itemCountPerBatch
          }))
        }
      })

      const currentObserver = observer.current
      if (secondToLastItem.current && savedEvents.length - oldItemsCount >= itemCountPerBatch) {
        currentObserver.observe(secondToLastItem.current)
      }

      setLoadingMore(false)

      return () => {
        if (secondToLastItem.current) {
          observer.current.disconnect()
        }
      }
    }, 1000)
  }, [savedEvents])

  useEffect(() => {
    if (getSavedEventsStatus === SUCCEEDED) {
      dispatch(setGetSavedEventsStatus(0))
    }
  }, [getSavedEventsStatus])

  return (
    <Box>
      {
        savedEvents.length
          ? savedEvents.map((item) => (
            <Box
              key={item?.id}
              ref={
                item?.id === savedEvents.at(-1)?.id
                  ? secondToLastItem
                  : null
              }
              height="90px"
              borderBottom="solid 2px black"
              overflow="hidden"
              position="relative"
              cursor={item?.placeholderOnly ? 'auto' : 'pointer'}
              display={item?.hide === 1 ? 'none' : 'flex'}
              onClick={() => {
                if (!item?.placeholderOnly) {
                  let waitTime = 0
                  if (isNotMobile()) {
                    const notifDrawer = document.querySelector('#NOTIFICATIONS-panel')
                    if (notifDrawer && notifDrawer.offsetWidth * 3 >= document.querySelector('body').offsetWidth) {
                      dispatch(setHideNotification(true))
                      dispatch(setHideChat(true))
                      dispatch(handleOpenModal({
                        isOpen: false,
                        section: MODAL_SECTION.RightSection
                      }))
                      waitTime = 900
                    }
                  }

                  setTimeout(() => {
                    dispatch(setEventComments([]))
                    dispatch(setEventCommentTree([]))
                    dispatch(getEventData(item?.eventId))
                    if (openPopupBlogs) {
                      dispatch(setOpenPopupBlogs(false))
                    }

                    setTimeout(() => {
                      dispatch(setOpenPopupEvents(true))
                    }, openPopupBlogs ? 850 : 0)
                  }, waitTime)
                }
              }}
            >
              <Skeleton
                isLoaded={loadedBG[item.id]}
                width="100%"
              >
                <Image
                  width="100%"
                  height="90px !important"
                  filter="blur(2px)"
                  objectFit="cover"
                  src={item?.background_image ?? ImagesPng.PlaceholderSquare}
                  onLoad={
                    item?.placeholderOnly
                      ? undefined
                      : () => handleBG(item?.id)
                  }
                />
              </Skeleton>

              <Text
                display={item?.placeholderOnly ? 'none' : 'block'}
                position="absolute"
                top="2px"
                left="3"
                paddingLeft="2px"
                width="100px"
                fontSize="22px"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                textShadow={
                  '0.6px 0 0 white, -0.6px 0 0 white, 0 0.6px 0 white, 0 -0.6px 0 white, '
                  + '0.6px 0.6px 0 white, -0.6px 0.6px 0 white, 0.6px -0.6px 0 white, -0.6px -0.6px 0 white'
                }
              >
                {item?.name}
              </Text>

              <Text
                display={item?.placeholderOnly ? 'none' : 'block'}
                position="absolute"
                top="38px"
                left="3"
                paddingLeft="2px"
                width="100px"
                fontSize="15px"
                textShadow={
                  '0.6px 0 0 white, -0.6px 0 0 white, 0 0.6px 0 white, 0 -0.6px 0 white, '
                  + '0.6px 0.6px 0 white, -0.6px 0.6px 0 white, 0.6px -0.6px 0 white, -0.6px -0.6px 0 white'
                }
              >
                {convertToOwnTimeZone(item?.start_date, 'Y.m.d')}
              </Text>

              <Text
                display={item?.placeholderOnly ? 'none' : 'block'}
                position="absolute"
                bottom="2px"
                left="3"
                paddingLeft="2px"
                width="100px"
                fontSize="15px"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                textShadow={
                  '0.6px 0 0 white, -0.6px 0 0 white, 0 0.6px 0 white, 0 -0.6px 0 white, '
                  + '0.6px 0.6px 0 white, -0.6px 0.6px 0 white, 0.6px -0.6px 0 white, -0.6px -0.6px 0 white'
                }
              >
                {item?.address ?? (decimalToDMS(item?.lat, 'lat') + ' ' + decimalToDMS(item?.lng, 'lng'))}
              </Text>

              <Text
                display={item?.placeholderOnly ? 'none' : 'block'}
                position="absolute"
                top="50%"
                left="50%"
                transform="translateX(-50%) translateY(-50%)"
                backgroundColor="rgba(255,255,255,0.6)"
                borderRadius="5px"
                width="45%"
                maxHeight="70px"
                padding="0 15px"
                letterSpacing="-1px"
                fontSize="15px"
                noOfLines={3}
              >
                {item?.description}
              </Text>

              <Box
                position="absolute"
                bottom="1"
                right="2"
                maxWidth="60%"
                overflowX="auto"
                overflowY="hidden"
                sx={{
                  '::-webkit-scrollbar': {
                    display: 'none'
                  },
                  '-ms-overflow-style': 'none',
                  'scrollbar-width': 'none'
                }}
              >
                <Stack
                  direction="row"
                  spacing="4px"
                  whiteSpace="nowrap"
                >
                  <RotatingAffinityText
                    key={item?.id}
                    affinities={item?.affinities}
                    style={{
                      backgroundColor: COLORS.gold,
                      border: 'solid 1px black',
                      borderRadius: '5px',
                      paddingLeft: '8px',
                      paddingRight: '8px',
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      display: 'inline-block',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden'
                    }}
                  />
                </Stack>
              </Box>

              <Box
                display={item?.placeholderOnly ? 'none' : 'block'}
                as="button"
                position="absolute"
                top="1"
                right="2"
                backgroundColor="rgba(255,255,255,0.6)"
                borderRadius="5px"
                onClick={(e) => {
                  e.stopPropagation()

                  dispatch(saveEvent({
                    id: item?.eventId,
                    action: 'remove'
                  }))
                }}
              >
                <CloseIcon
                  width="21px"
                  height="22px"
                />
              </Box>
            </Box>
          )) : (
            <Box
              alignItems="center"
              backgroundColor="rgb(229, 229, 229)"
              borderRadius="3px"
              border="solid 1px black"
              margin="5% 2% !important"
              padding="5% 10%"
            >
              <Text
                textAlign="center"
                whiteSpace="pre-line"
              >
                No Saved Events Yet
              </Text>
            </Box>
          )
      }

      {
        loadingMore && getSavedEventsStatus === PENDING
          ? (
            <Box margin="75px 0 75px 0">
              <LoadingSpinier />
            </Box>
          ) : ''
      }
    </Box>
  )
}
